// ---------------------------------------------------------------------------
// Sprachen der Anwendung: Deutsch und Englisch, gleichrangig.
//
// Grundlage ist i18next mit react-i18next. Das ist der verbreitete Standard
// fuer React und bringt genau das mit, was hier gebraucht wird: Namensraeume
// je Seite, Platzhalter, Mehrzahlformen und eine Sprache, die an einer Stelle
// gehalten wird.
//
// AUFBAU
//   src/i18n/index.js      diese Datei, Einrichtung und Sprachwechsel
//   src/i18n/de/<raum>.json  deutsche Texte je Seite
//   src/i18n/en/<raum>.json  englische Texte je Seite
//
// EINE NEUE SEITE UEBERSETZEN
//   1. Zwei Dateien anlegen: de/meineseite.json und en/meineseite.json
//   2. Beide unten in KATALOG eintragen
//   3. In der Seite: const { t } = useTranslation("meineseite");
//      dann t("titel") statt des festen Textes
//
// Der Schluessel ist die Adresse des Textes, nicht der Text selbst. So bleibt
// er stabil, wenn sich die Formulierung aendert.
//
// DEUTSCH IST KEINE UEBERSETZUNG DES ENGLISCHEN. Beide Fassungen werden
// eigenstaendig geschrieben. Deutsche Beschriftungen sind gegenueber
// englischen typisch ein Fuenftel bis ein Drittel laenger; feste Breiten, die
// genau eine Sprache fassen, scheiden deshalb aus.
// ---------------------------------------------------------------------------

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// --- Katalog ---------------------------------------------------------------
// Statische Einbindung statt Nachladen: die Anwendung laeuft hinter einer
// Anmeldung, die Textmengen sind klein, und ein Nachladen brächte hier nur
// einen zweiten Fehlerfall ohne spuerbaren Gewinn.
import deGemeinsam from "./de/gemeinsam.json";
import enGemeinsam from "./en/gemeinsam.json";
import deNavigation from "./de/navigation.json";
import enNavigation from "./en/navigation.json";
import deSpaces from "./de/spaces.json";
import enSpaces from "./en/spaces.json";
import deCustomers from "./de/customers.json";
import enCustomers from "./en/customers.json";
import deEinstellungen from "./de/einstellungen.json";
import enEinstellungen from "./en/einstellungen.json";
import deSpace from "./de/space.json";
import enSpace from "./en/space.json";
import deMonitor from "./de/monitor.json";
import enMonitor from "./en/monitor.json";
import deContent from "./de/content.json";
import enContent from "./en/content.json";
import deAlerting from "./de/alerting.json";
import enAlerting from "./en/alerting.json";
import dePartnerDirectory from "./de/partnerdirectory.json";
import enPartnerDirectory from "./en/partnerdirectory.json";
import deAuditLog from "./de/auditlog.json";
import enAuditLog from "./en/auditlog.json";
import dePipeline from "./de/pipeline.json";
import enPipeline from "./en/pipeline.json";
import deCicdSetup from "./de/cicdsetup.json";
import enCicdSetup from "./en/cicdsetup.json";
import deCicdAutomations from "./de/cicdautomations.json";
import enCicdAutomations from "./en/cicdautomations.json";
import deCicdRuns from "./de/cicdruns.json";
import enCicdRuns from "./en/cicdruns.json";
import deCicdMonitor from "./de/cicdmonitor.json";
import enCicdMonitor from "./en/cicdmonitor.json";
import deFreigabe from "./de/freigabe.json";
import enFreigabe from "./en/freigabe.json";
import deDoku from "./de/doku.json";
import enDoku from "./en/doku.json";
import deBenutzer from "./de/benutzer.json";
import enBenutzer from "./en/benutzer.json";
import deKi from "./de/ki.json";
import enKi from "./en/ki.json";
import deBuilder from "./de/builder.json";
import enBuilder from "./en/builder.json";
import deSchleuse from "./de/schleuse.json";
import enSchleuse from "./en/schleuse.json";
import dePixelArt from "./de/pixelart.json";
import enPixelArt from "./en/pixelart.json";
import dePastebin from "./de/pastebin.json";
import enPastebin from "./en/pastebin.json";
import deMonitoring from "./de/monitoring.json";
import enMonitoring from "./en/monitoring.json";
import deErrorMonitorCosts from "./de/errorMonitorCosts.json";
import enErrorMonitorCosts from "./en/errorMonitorCosts.json";
import deIflowDocumentationPdf from "./de/iflowDocumentationPdf.json";
import enIflowDocumentationPdf from "./en/iflowDocumentationPdf.json";

const KATALOG = {
  de: {
    iflowDocumentationPdf: deIflowDocumentationPdf,
    gemeinsam: deGemeinsam,
    navigation: deNavigation,
    spaces: deSpaces,
    customers: deCustomers,
    einstellungen: deEinstellungen,
    space: deSpace,
    monitor: deMonitor,
    content: deContent,
    alerting: deAlerting,
    partnerdirectory: dePartnerDirectory,
    auditlog: deAuditLog,
    pipeline: dePipeline,
    cicdsetup: deCicdSetup,
    cicdmonitor: deCicdMonitor,
    cicdautomations: deCicdAutomations,
    cicdruns: deCicdRuns,
    freigabe: deFreigabe,
    doku: deDoku,
    benutzer: deBenutzer,
    ki: deKi,
    builder: deBuilder,
    schleuse: deSchleuse,
    pixelart: dePixelArt,
    pastebin: dePastebin,
    monitoring: deMonitoring,
    errorMonitorCosts: deErrorMonitorCosts,
  },
  en: {
    iflowDocumentationPdf: enIflowDocumentationPdf,
    gemeinsam: enGemeinsam,
    navigation: enNavigation,
    spaces: enSpaces,
    customers: enCustomers,
    einstellungen: enEinstellungen,
    space: enSpace,
    monitor: enMonitor,
    content: enContent,
    alerting: enAlerting,
    partnerdirectory: enPartnerDirectory,
    auditlog: enAuditLog,
    pipeline: enPipeline,
    cicdsetup: enCicdSetup,
    cicdmonitor: enCicdMonitor,
    cicdautomations: enCicdAutomations,
    cicdruns: enCicdRuns,
    freigabe: enFreigabe,
    doku: enDoku,
    benutzer: enBenutzer,
    ki: enKi,
    builder: enBuilder,
    schleuse: enSchleuse,
    pixelart: enPixelArt,
    pastebin: enPastebin,
    monitoring: enMonitoring,
    errorMonitorCosts: enErrorMonitorCosts,
  },
};

export const SPRACHEN = [
  { code: "de", name: "Deutsch" },
  { code: "en", name: "English" },
];

// Derselbe Schluessel, den der Transport-Assistent seit jeher benutzt. Damit
// teilen sich beide Bereiche eine Sprache, statt zwei Zustaende zu fuehren.
export const SPEICHER_SCHLUESSEL = "iio-lang";

function startSprache() {
  try {
    const gemerkt = localStorage.getItem(SPEICHER_SCHLUESSEL);
    if (gemerkt === "de" || gemerkt === "en") return gemerkt;
  } catch { /* privater Modus ohne Speicher: dann eben die Voreinstellung */ }
  const vomBrowser = (navigator.language || "de").slice(0, 2).toLowerCase();
  return vomBrowser === "en" ? "en" : "de";
}

i18n.use(initReactI18next).init({
  resources: KATALOG,
  lng: startSprache(),
  fallbackLng: "de",
  defaultNS: "gemeinsam",
  interpolation: {
    escapeValue: false, // React schuetzt selbst vor Einschleusung
  },
  returnEmptyString: false,
  // Fehlt ein Schluessel, erscheint der Schluessel selbst. Das faellt beim
  // Sichttest sofort auf, statt still einen leeren Bereich zu hinterlassen.
});

/**
 * Sprache umschalten und merken.
 * @param {"de"|"en"} code
 */
export function setzeSprache(code) {
  if (code !== "de" && code !== "en") return;
  i18n.changeLanguage(code);
  // Daran haengen zwei Dinge ausserhalb von React: formatDate in helpers.js und
  // der Accept-Language-Header, mit dem die Servermeldungen uebersetzt werden.
  // Bisher wurde es nur als Nebenwirkung vom Transport-Assistenten mitgezogen -
  // eine zufaellige Abhaengigkeit, die beim Ausbau still gebrochen waere.
  if (typeof window !== "undefined") window.__iioLang = code;
  try { localStorage.setItem(SPEICHER_SCHLUESSEL, code); } catch { /* siehe oben */ }
  // Der Transport-Assistent hoert auf dieses Ereignis, weil er seinen eigenen
  // Katalog fuehrt. So bleibt die Sprache ueber die ganze Anwendung dieselbe.
  window.dispatchEvent(new CustomEvent("iio-lang-change", { detail: code }));
  document.documentElement.setAttribute("lang", code);
}

// Gegenrichtung: der Transport-Assistent fuehrt seinen eigenen Katalog und
// meldet einen Wechsel ueber dasselbe Ereignis. Hier NICHT erneut melden,
// sonst laufen beide Seiten im Kreis.
window.addEventListener("iio-lang-change", (e) => {
  const code = e && e.detail;
  if (code !== "de" && code !== "en") return;
  if (i18n.language === code) return;
  i18n.changeLanguage(code);
  document.documentElement.setAttribute("lang", code);
});

document.documentElement.setAttribute("lang", i18n.language);
if (typeof window !== "undefined") window.__iioLang = i18n.language;

export default i18n;
